import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Sparkles, Wine, Megaphone, X, BadgePercent, Eye, MousePointerClick, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDiscounts, fmtRate } from "@/lib/useDiscounts";

export const NOTIF_KINDS = {
  arrivage: { label: "Arrivage", icon: Wine },
  offre: { label: "Offre spéciale", icon: Sparkles },
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

// Barre de statistique d'une campagne (vues, clics, push)
function StatBar({ icon: Icon, label, value, total, accent = "#6B1226", testid }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-[0.65rem] font-semibold text-[#57534E]">
        <span className="inline-flex items-center gap-1"><Icon size={11} style={{ color: accent }} /> {label}</span>
        <span className="text-[#1C1917]">{value}{total > 0 ? ` / ${total}` : ""} <span className="text-[#A8A29E] font-normal">({pct} %)</span></span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-[#6B1226]/10 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: accent }} />
      </div>
    </div>
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [pushState, setPushState] = useState("unknown");
  const panelRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const load = () =>
      api
        .get("/notifications/mine")
        .then((res) => {
          setUnread(res.data.unread);
          setItems(res.data.items);
          if (navigator.setAppBadge) {
            if (res.data.unread > 0) navigator.setAppBadge(res.data.unread).catch(() => {});
            else navigator.clearAppBadge?.().catch(() => {});
          }
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
      setPushState("unsupported");
      return;
    }
    setPushState(Notification.permission === "granted" ? "granted" : "off");
  }, [user]);

  useEffect(() => {
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("pointerdown", onClick);
    return () => document.removeEventListener("pointerdown", onClick);
  }, [open]);

  if (!user) return null;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      try {
        const res = await api.get("/notifications/mine");
        setItems(res.data.items);
        if (res.data.unread > 0) {
          await api.post("/notifications/read-all");
          setUnread(0);
          navigator.clearAppBadge?.().catch(() => {});
        }
      } catch {}
    }
  };

  const enablePush = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Notifications refusées par le téléphone");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const res = await api.get("/push/vapid-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(res.data.key),
      });
      await api.post("/push/subscribe", sub.toJSON());
      setPushState("granted");
      toast.success("Notifications activées sur cet appareil");
    } catch (err) {
      toast.error("Impossible d'activer les notifications ici");
    }
  };

  const openNotif = (n) => {
    api.post(`/notifications/${n.id}/click`).catch(() => {});
    setOpen(false);
    navigate("/vins");
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={toggle}
        className="relative rounded-full bg-[#F5EEDC] text-[#8C6D23] border border-[#C5A059]/60 flex items-center justify-center hover:bg-[#6B1226] hover:text-[#FAF6F0] hover:border-[#6B1226] transition-colors"
        style={{ width: "7.4cqw", height: "7.4cqw", minWidth: "30px", minHeight: "30px" }}
        data-testid="notifications-bell"
        aria-label="Notifications"
      >
        <Bell style={{ width: "4cqw", height: "4cqw", minWidth: "15px", minHeight: "15px" }} strokeWidth={1.7} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 rounded-full bg-[#6B1226] text-[#FAF6F0] font-bold flex items-center justify-center border-2 border-[#FEF9F6]"
            style={{ minWidth: "3.6cqw", height: "3.6cqw", fontSize: "2.2cqw", padding: "0 0.6cqw" }}
            data-testid="notifications-unread-badge"
          >
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[82vw] max-w-[340px] bg-[#FAF6F0] rounded-2xl border border-[#6B1226]/15 shadow-2xl z-50 overflow-hidden"
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#6B1226]/10">
            <p className="font-display text-lg font-semibold text-[#1C1917]">Notifications</p>
            <button onClick={() => setOpen(false)} className="p-1 text-[#78716C] hover:text-[#6B1226]" data-testid="notifications-close" aria-label="Fermer">
              <X size={16} />
            </button>
          </div>
          <div className="max-h-[55vh] overflow-y-auto no-scrollbar" data-lenis-prevent>
            {pushState === "off" && (
              <button
                onClick={enablePush}
                className="w-full text-left px-4 py-3 bg-[#F5EEDC] border-b border-[#C5A059]/40 text-xs font-semibold text-[#8C6D23] flex items-center gap-2 hover:bg-[#6B1226] hover:text-[#FAF6F0] transition-colors"
                data-testid="notifications-enable-push"
              >
                <Bell size={14} /> Activer les notifications sur cet appareil
              </button>
            )}
            {items.length === 0 ? (
              <p className="text-sm text-[#78716C] italic text-center py-8" data-testid="notifications-empty">
                Aucune notification pour l'instant.
              </p>
            ) : (
              items.map((n) => {
                const kind = NOTIF_KINDS[n.kind] || NOTIF_KINDS.arrivage;
                const Icon = kind.icon;
                return (
                  <button key={n.id} onClick={() => openNotif(n)} className="w-full text-left px-4 py-3.5 border-b border-[#6B1226]/8 last:border-0 hover:bg-[#F5EEDC]/60 transition-colors cursor-pointer" data-testid="notification-item">
                    <p className="flex items-center gap-1.5 font-eyebrow text-[0.55rem] tracking-[0.2em] uppercase font-bold text-[#C5A059]">
                      <Icon size={11} /> {kind.label}
                    </p>
                    <p className="font-semibold text-sm text-[#1C1917] mt-1">{n.title}</p>
                    <p className="text-xs text-[#57534E] mt-0.5 leading-relaxed">{n.body}</p>
                    <p className="text-[0.6rem] text-[#A8A29E] mt-1.5">{fmtDate(n.created_at)}</p>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminNotifications() {
  const [items, setItems] = useState(null);
  const [totalClients, setTotalClients] = useState(0);
  const [form, setForm] = useState({ title: "", body: "", kind: "arrivage" });
  const [busy, setBusy] = useState(false);
  const [producers, setProducers] = useState([]);
  const tiersMap = useDiscounts();

  // Viticulteurs présents à la carte (pour les modèles par producteur)
  useEffect(() => {
    api
      .get("/wines")
      .then((res) => {
        const list = [...new Set(res.data.map((w) => w.producer).filter(Boolean))];
        setProducers(list);
      })
      .catch(() => setProducers([]));
  }, []);

  // Modèles prêts à l'envoi, générés pour chaque viticulteur
  const producerPresets = producers.map((p) => {
    const presets = [
      { kind: "arrivage", title: `Nouvelle maison à la cave : ${p}`, body: `Grande nouvelle : la maison ${p} rejoint la carte du Bistroquet ! Découvrez dès maintenant sa sélection dans l'application — commandez en ligne, retrait au restaurant de Belfort.` },
      { kind: "arrivage", title: `Nouvel arrivage ${p}`, body: `De nouveaux vins de ${p} viennent d'arriver à la cave du Bistroquet. Venez les découvrir dès maintenant dans l'application !` },
      { kind: "arrivage", title: `Nouveau produit : ${p}`, body: `Une nouvelle référence ${p} rejoint la carte du Bistroquet. Venez la découvrir et réservez vos bouteilles !` },
    ];
    const tiers = tiersMap[p];
    if (tiers && tiers.length) {
      presets.push({
        kind: "offre",
        title: `Remise panachée ${p}`,
        body: `Profitez de la remise panachée ${p} : ${tiers.map((t) => `${fmtRate(t.rate)} dès ${t.threshold} bouteilles`).join(", ")}, en mélangeant librement les vins de ce viticulteur !`,
      });
    }
    return { producer: p, presets };
  });

  const PRESETS_GENERAL = [
    { kind: "arrivage", title: "Bientôt à la carte : les champagnes", body: "Les champagnes et les coffrets cadeaux arrivent prochainement à la carte. Nous vous préviendrons dès leur mise en ligne." },
    { kind: "offre", title: "Offre spéciale du moment", body: "Offre spéciale cette semaine au Bistroquet : découvrez notre sélection à prix caviste, retrait au restaurant de Belfort." },
    { kind: "offre", title: "Dernières bouteilles disponibles", body: "Il ne reste plus que quelques bouteilles de certaines références de la cave. Commandez les vôtres avant rupture !" },
  ];

  const applyPreset = (p) => {
    setForm({ title: p.title, body: p.body, kind: p.kind });
    toast.success("Modèle chargé — vous pouvez l'ajuster avant l'envoi");
  };

  const load = () =>
    api
      .get("/admin/notifications")
      .then((res) => {
        setItems(res.data.items);
        setTotalClients(res.data.total_clients);
      })
      .catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/notifications", form);
      toast.success("Notification envoyée à tous les clients");
      setForm({ title: "", body: "", kind: "arrivage" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 space-y-6" data-testid="admin-notifications">
      <form onSubmit={submit} className="bg-white rounded-2xl border border-[#6B1226]/10 p-5 space-y-3" data-testid="admin-notification-form">
        <p className="font-display text-xl font-semibold text-[#1C1917] flex items-center gap-2">
          <Megaphone size={18} className="text-[#6B1226]" /> Informer les clients
        </p>
        <div data-testid="notification-presets">
          {producerPresets.map(({ producer, presets }) => (
            <div key={producer} className="mb-3" data-testid="notification-preset-producer">
              <p className="eyebrow mb-2 flex items-center gap-1.5">
                <Wine size={11} className="text-[#6B1226]" /> {producer}
              </p>
              <div className="flex flex-wrap gap-2">
                {presets.map((p, i) => (
                  <button
                    type="button"
                    key={i}
                    onClick={() => applyPreset(p)}
                    className="inline-flex items-center gap-1 rounded-full bg-[#F5EEDC] border border-[#C5A059]/50 text-[#8C6D23] px-3.5 py-1.5 text-[0.7rem] font-semibold hover:bg-[#6B1226] hover:text-[#FAF6F0] hover:border-[#6B1226] transition-colors"
                    data-testid={`notification-preset-${producer}-${i}`}
                  >
                    {p.kind === "offre" ? <BadgePercent size={11} /> : null}
                    {p.title}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <p className="eyebrow mb-2">Modèles généraux</p>
          <div className="flex flex-wrap gap-2">
            {PRESETS_GENERAL.map((p, i) => (
              <button
                type="button"
                key={i}
                onClick={() => applyPreset(p)}
                className="rounded-full bg-[#F5EEDC] border border-[#C5A059]/50 text-[#8C6D23] px-3.5 py-1.5 text-[0.7rem] font-semibold hover:bg-[#6B1226] hover:text-[#FAF6F0] hover:border-[#6B1226] transition-colors"
                data-testid={`notification-preset-general-${i}`}
              >
                {p.title}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          {Object.entries(NOTIF_KINDS).map(([id, k]) => (
            <button
              type="button"
              key={id}
              onClick={() => setForm({ ...form, kind: id })}
              className={`flex-1 rounded-full px-4 py-2.5 text-xs font-semibold tracking-wide transition-colors ${
                form.kind === id ? "bg-[#6B1226] text-[#FAF6F0]" : "bg-white border border-[#6B1226]/15 text-[#57534E]"
              }`}
              data-testid={`notification-kind-${id}`}
            >
              {k.label}
            </button>
          ))}
        </div>
        <input
          required
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Titre (ex. Arrivage Gigondas 2023)"
          className="w-full bg-white border border-[#6B1226]/15 rounded-xl px-4 py-3 text-base outline-none focus:border-[#6B1226]/50 transition-colors"
          data-testid="notification-title-input"
        />
        <textarea
          required
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          placeholder="Votre annonce…"
          rows={3}
          className="w-full bg-white border border-[#6B1226]/15 rounded-xl px-4 py-3 text-base outline-none focus:border-[#6B1226]/50 transition-colors resize-none"
          data-testid="notification-body-input"
        />
        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-full bg-[#6B1226] text-[#FAF6F0] py-3.5 text-sm font-bold hover:bg-[#580E1D] transition-colors disabled:opacity-60"
          data-testid="notification-send-button"
        >
          {busy ? "Envoi…" : "Envoyer à tous les clients"}
        </button>
      </form>

      <div>
        <p className="eyebrow mb-3">Historique des annonces — {totalClients} client{totalClients > 1 ? "s" : ""} inscrit{totalClients > 1 ? "s" : ""}</p>
        {items === null ? (
          <p className="text-sm text-[#57534E] italic">Chargement…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#78716C] italic" data-testid="admin-notifications-empty">Aucune annonce envoyée pour l'instant.</p>
        ) : (
          <ul className="space-y-3" data-testid="admin-notifications-list">
            {items.map((n) => {
              const kind = NOTIF_KINDS[n.kind] || NOTIF_KINDS.arrivage;
              const views = n.read_by?.length || 0;
              const clicks = n.clicked_by?.length || 0;
              return (
                <li key={n.id} className="bg-white rounded-2xl border border-[#6B1226]/10 p-4" data-testid="admin-notification-item">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-eyebrow text-[0.55rem] tracking-[0.2em] uppercase font-bold text-[#C5A059]">{kind.label}</span>
                    <span className="text-[0.65rem] text-[#A8A29E]">{fmtDate(n.created_at)}</span>
                  </div>
                  <p className="font-semibold text-sm text-[#1C1917] mt-1.5">{n.title}</p>
                  <p className="text-xs text-[#57534E] mt-0.5">{n.body}</p>
                  <div className="mt-3 pt-3 border-t border-[#6B1226]/8 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2.5" data-testid="campaign-stats">
                    <StatBar icon={Eye} label="Vues" value={views} total={totalClients} accent="#6B1226" testid="campaign-stat-views" />
                    <StatBar icon={MousePointerClick} label="Clics" value={clicks} total={views} accent="#8C6D23" testid="campaign-stat-clicks" />
                    <StatBar icon={Smartphone} label="Push envoyés" value={n.push_sent || 0} total={(n.push_sent || 0) + (n.push_failed || 0)} accent="#3F6212" testid="campaign-stat-push" />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
